import { useMemo, useState } from 'react';
import Carousel, { ResponsiveType } from 'react-multi-carousel';
import { TImage } from '../../types';
import { Dialog } from '@headlessui/react';

import './ImageCarousel.css';

type TProps = {
  images: TImage[];
  readonly?: boolean;
  onRemoveItem?: (image: TImage) => void;
  itemClassName?: string;
  containerClassName?: string;
  itemsShown?: {
    superLargeDesktop?: number;
    desktop?: number;
    tablet?: number;
    mobile?: number;
    smallDesktop?: number;
  };
  showDots?: boolean;
  showFullImageOnClick?: boolean;
};

const ImageCarousel = ({
  images,
  onRemoveItem,
  readonly,
  containerClassName,
  itemsShown,
  itemClassName = '',
  showDots = true,
  showFullImageOnClick = false,
}: TProps) => {
  const [displayedImage, setDisplayedImage] = useState('');

  const responsive = useMemo<ResponsiveType>(() => {
    const xlDesktopItems = itemsShown?.superLargeDesktop || 5;
    const desktopItems = itemsShown?.desktop || 3;
    const smallDesktopItems = itemsShown?.smallDesktop || 2;
    const tabletItems = itemsShown?.tablet || 2;
    const mobileItems = itemsShown?.mobile || 1;

    return {
      superLargeDesktop: {
        breakpoint: { max: 4000, min: 2000 },
        items: xlDesktopItems,
      },
      desktop: {
        breakpoint: { max: 2000, min: 1600 },
        items: desktopItems,
      },
      smallDesktop: {
        breakpoint: { max: 1600, min: 1024 },
        items: smallDesktopItems,
      },
      tablet: {
        breakpoint: { max: 1024, min: 651 },
        items: tabletItems,
      },
      mobile: {
        breakpoint: { max: 651, min: 0 },
        items: mobileItems,
      },
    };
  }, [
    itemsShown?.desktop,
    itemsShown?.smallDesktop,
    itemsShown?.mobile,
    itemsShown?.superLargeDesktop,
    itemsShown?.tablet,
  ]);

  return (
    <>
      <Carousel
        responsive={responsive}
        slidesToSlide={1}
        className="w-full"
        itemClass="w-auto"
        containerClass={containerClassName}
        showDots={showDots}
        arrows
      >
        {images.map((i) => (
          <div
            key={i.id}
            className={`relative ${
              showFullImageOnClick ? 'cursor-pointer' : ''
            } ${itemClassName}`}
            onClick={() => setDisplayedImage(i.src)}
          >
            {!readonly && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (onRemoveItem) {
                    onRemoveItem(i);
                  }
                }}
                className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-[25px] h-[26px]"
              >
                x
              </button>
            )}
            <img
              className="object-cover h-full w-full rounded-md"
              src={i.src}
              alt={i.id}
            />
          </div>
        ))}
      </Carousel>

      {showFullImageOnClick && (
        <Dialog open={!!displayedImage} onClose={() => setDisplayedImage('')}>
          <Dialog.Overlay className="fixed inset-0 bg-black opacity-50" />
          <div className="fixed inset-0 flex items-center justify-center z-[1001]">
            <Dialog.Panel className="bg-transparent p-2 flex items-center justify-center min-h-[50%] lg:min-w-[50%] min-w-[99%] max-h-[80%] lg:max-w-[80%] max-w-[99%]">
              <img
                src={displayedImage}
                alt="displayed"
                className="w-full h-full object-contain rounded-md"
                onClick={() => setDisplayedImage('')}
              />
            </Dialog.Panel>
          </div>
        </Dialog>
      )}
    </>
  );
};

export default ImageCarousel;
